<script setup lang="ts">
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const baseConfirmController = useBaseConfirmController();
const {
  isOpen,
  title,
  message,
  isMessageComponent,
  confirmButtonLabel,
  cancelButtonLabel,
  autoFocusButton,
} = storeToRefs(baseConfirmController);

const confirm = (accept: boolean) => {
  baseConfirmController.close();
  baseConfirmController.savedResolver(accept);
};

const onOpenAutoFocus = (event: Event) => {
  if (autoFocusButton.value) return;
  event.preventDefault();
  // Keep focus inside the dialog without highlighting either action.
  (event.target as HTMLElement | null)?.focus();
};

</script>

<template>
  <Dialog v-model:open="isOpen" class="bg-black/50" @close="confirm(false)">
    <DialogTrigger class="hidden">
      <slot name="trigger" />
    </DialogTrigger>
    <DialogContent
      class="z-[1000002]"
      overlay-class="z-[1000001]"
      @open-auto-focus="onOpenAutoFocus"
    >
      <DialogHeader>
        <DialogTitle>
          <slot name="title">
            {{ title }}
          </slot>
        </DialogTitle>
        <DialogDescription>
          <slot name="description">
            <component :is="message" v-if="isMessageComponent" />
            <span v-else class="whitespace-pre-line">{{ message }}</span>
          </slot>
        </DialogDescription>
      </DialogHeader>
      <slot name="content" />
      <DialogFooter>
        <BaseOutlinedButton
          class="min-w-[96px] !h-[35px] whitespace-nowrap"
          color="danger"
          @click="confirm(false)"
        >
          {{ cancelButtonLabel }}
        </BaseOutlinedButton>
        <BaseButton
          class="min-w-[96px] !h-[35px] whitespace-nowrap"
          @click="confirm(true)"
        >
          {{ confirmButtonLabel }}
        </BaseButton>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>

<style scoped></style>
